import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Badge,
  Pagination,
  Table,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import {
  FaCheckCircle,
  FaTimesCircle
} from "react-icons/fa";
const BORDER_COLORS = {
  Vacation: "#4da6ff",
  Sick: "#ff6666",
  Personal: "#ffcc66",
  Other: "#ffa500",
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_URL;

export default function AdminPendingLeaves({ fetchApprovedRequests }) {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const currentAdminEmail = storedUser.email || "";
  const currentAdminName = storedUser.name || "Admin";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(new Set());

  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [decisionType, setDecisionType] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const socketRef = useRef(null);

  const sortByDate = (a, b) => new Date(b.created_at) - new Date(a.created_at);

  const shouldAdminSeeRequest = (req, adminEmail) => {
    const toMatch = req.to_email.toLowerCase() === adminEmail.toLowerCase();
    const ccList = req.cc_email
      ? req.cc_email.split(",").map((e) => e.trim().toLowerCase())
      : [];
    return toMatch || ccList.includes(adminEmail.toLowerCase());
  };

  const fetchPending = useCallback(() => {
    setLoading(true);
    setError("");
    axios
      .get(`${API_URL}/api/pending-requests`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const pendingForAdmin = data.filter((r) =>
          shouldAdminSeeRequest(r, currentAdminEmail)
        );
        setRequests(pendingForAdmin.sort(sortByDate));
      })
      .catch(() => setError("Failed to load pending requests"))
      .finally(() => setLoading(false));
  }, [currentAdminEmail]);

  useEffect(() => {
    fetchPending();

    socketRef.current = io(SOCKET_URL);

    socketRef.current.on("newPendingRequest", (newReq) => {
      if (shouldAdminSeeRequest(newReq, currentAdminEmail)) {
        setRequests((prev) => [newReq, ...prev].sort(sortByDate));
        toast.info("New pending request received");
      }
    });

    socketRef.current.on("requestDecision", (payload) => {
      setRequests((prev) =>
        prev.filter((r) => r.request_id !== payload.requestId)
      );
      if (payload.decision === "approved") {
        toast.success(`Request #${payload.requestId} approved`);
      } else {
        toast.warn(`Request #${payload.requestId} rejected`);
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [fetchPending, currentAdminEmail]);

  const prepareEmailTemplate = (req, decision) => {
    const subj = req.subject;
    const decisionText = decision === "approved" ? "approved" : "not approved";
    const body =
      `Hello ${req.from_name},\n\n` +
      `Your ${req.leave_type} request submitted on ${new Date(req.created_at).toLocaleDateString("en-GB")}
 has been ${decisionText} by ${currentAdminName}.\n\n` +
      `Best regards,\n${currentAdminName}`;
    return { subj, body };
  };

  const openEmailEditor = (decision) => {
    if (!selected) return;
    const { subj, body } = prepareEmailTemplate(selected, decision);

    setDecisionType(decision);
    setEmailSubject(subj);
    setEmailBody(body || "");
    setSaveEmail(selected.body);
    setEmailModalVisible(true);
  };

  const handleSendEmail = useCallback(async () => {
    if (!selected) return;

    setModalLoading(true);
    setProcessing((prev) => new Set(prev).add(selected.request_id));

    try {
      // Parse leave details from the selected request
      const leaveDetails = parseLeaveDetails(selected);

// Format dates for backend (YYYY-MM-DD)
const formattedDates = leaveDetails.dates.map(date => {
  try {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  } catch {
    return date;
  }
});

// ✅ Pad days and types to match dates length
const paddedDays = formattedDates.map((_, i) =>
  leaveDetails.days[i] || leaveDetails.days[0] || "Full"
);
const paddedTypes = formattedDates.map((_, i) =>
  leaveDetails.types[i] || leaveDetails.types[0] || selected.leave_type
);
const totalDays = paddedDays.reduce((sum, d) =>
  sum + (d.toLowerCase() === "half" ? 0.5 : 1), 0
);

// Send the data to backend - match the backend expected format
const requestData = {
  decision: decisionType,
  emailBody: saveEmail || emailBody,
  subject: selected.subject || "",
  empName: selected.from_name,
  leave_type: selected.leave_type,
  leaveDetails: {
    dates: formattedDates,
    days: paddedDays,       // ✅ fixed
    types: paddedTypes,     // ✅ fixed
    totalDays: totalDays    // ✅ fixed
  },
  leaveDates: formattedDates,
  leaveDayTypes: paddedTypes,  // ✅ fixed
  leaveDays: paddedDays,       // ✅ fixed
  date: selected.date
};
      // Send to decision endpoint
      const decisionResponse = await axios.post(
        `${API_URL}/api/requests/${selected.request_id}/decision`,
        requestData
      );

      // Send decision email
      const finalCc = selected.cc_email
        ? `${selected.cc_email},${currentAdminEmail}`
        : currentAdminEmail;

      const emailResponse = await axios.post(`${API_URL}/api/send-decision-email`, {
        to_email: selected.from_email,
        cc_email: finalCc,
        subject: emailSubject,
        body: emailBody,
        from_name: selected.from_name,
        leave_dates: formattedDates.join(', '),
        total_days: leaveDetails.dates.length
      });

      toast.success(
        `Request ${decisionType === "approved" ? "approved" : "rejected"} and email sent`
      );

      // Update local state
      setRequests((prev) =>
        prev.filter((r) => r.request_id !== selected.request_id)
      );
      setSelected(null);
      setEmailModalVisible(false);

      // Refresh approved requests
      await fetchApprovedRequests();

    } catch (err) {
      console.error("❌ Error processing request:", err);
      console.error("Error details:", err.response?.data || err.message);

      if (err.response) {
        console.error("Response status:", err.response.status);
        console.error("Response data:", err.response.data);
        console.error("Response headers:", err.response.headers);
      }

      const action = err.response?.config?.url.includes("/decision")
        ? "submitting decision"
        : "sending email";
      toast.error(`Failed ${action}: ${err.response?.data?.error || err.message}`);
    } finally {
      setProcessing((prev) => {
        const copy = new Set(prev);
        copy.delete(selected.request_id);
        return copy;
      });
      setModalLoading(false);
    }
  }, [
    selected,
    decisionType,
    emailSubject,
    emailBody,
    currentAdminEmail,
    fetchApprovedRequests,
    saveEmail
  ]);
  const parseLeaveDetails = (request) => {
    if (!request) {
      return {
        dates: [],
        days: [],
        types: [],
        totalDays: 0,
        wfhdates: [],
        wfhdays: [],
        totalWFHDays: 0,
        compWorkingDates: [],
        compOffDates: [],
        compDurations: [],
        compRequestTypes: [],
        compAmounts: [],
        totalCompDays: 0,
        travelLocations: [],
        travelFromDatetimes: [],
        travelToDatetimes: [],
        travelApprovedStatuses: [],
        totalTravelRecords: 0
      };
    }

    // Leave details
    const dates = request.leave_dates
      ? request.leave_dates.split(',').map(d => d.trim())
      : [];

    const wfhdates = request.wfh_dates
      ? request.wfh_dates.split(',').map(d => d.trim())
      : [];

    const days = request.leave_days
      ? request.leave_days.split(',').map(d => d.trim())
      : [];

    const wfhdays = request.wfh_days
      ? request.wfh_days.split(',').map(d => d.trim())
      : [];

    const types = request.leave_day_types
      ? request.leave_day_types.split(',').map(t => t.trim())
      : [];

    // Compensatory off details
    const compWorkingDates = request.compensatory_working_dates
      ? request.compensatory_working_dates.split(',').map(d => d.trim())
      : [];

    const compOffDates = request.compensatory_off_dates
      ? request.compensatory_off_dates.split(',').map(d => d.trim())
      : [];

    const compDurations = request.compensatory_durations
      ? request.compensatory_durations.split(',').map(d => d.trim())
      : [];

    const compRequestTypes = request.compensatory_request_types
      ? request.compensatory_request_types.split(',').map(t => t.trim())
      : [];

    const compAmounts = request.compensation_amounts
      ? request.compensation_amounts.split(',').map(a => a.trim())
      : [];

    // ✅ Calculate total days
    const totalDays = days.reduce((sum, day) => {
      const value = day.toLowerCase();
      if (value === "half") return sum + 0.5;
      return sum + 1; // default = Full
    }, 0);

    const totalWFHDays = wfhdays.reduce((sum, day) => {
      const value = day.toLowerCase();
      if (value === "half") return sum + 0.5;
      return sum + 1; // default = Full
    }, 0);

    // Calculate compensatory days (based on durations)
    const totalCompDays = compDurations.reduce((sum, duration) => {
      const value = duration?.toLowerCase() || '';
      if (value.includes('half') || value.includes('0.5') || value === 'half day') return sum + 0.5;
      if (value.includes('full') || value.includes('1') || value === 'full day') return sum + 1;
      // Try to extract numeric value from duration
      const numericMatch = value.match(/(\d+(\.\d+)?)/);
      if (numericMatch) return sum + parseFloat(numericMatch[1]);
      return sum + 1; // default = Full
    }, 0);

    // ADD THESE ↓ (paste before the return statement)
    const travelLocations = request.travel_locations
      ? request.travel_locations.split(', ').map(d => d.trim())
      : [];

    const travelFromDatetimes = request.travel_from_datetimes
      ? request.travel_from_datetimes.split(', ').map(d => d.trim())
      : [];

    const travelToDatetimes = request.travel_to_datetimes
      ? request.travel_to_datetimes.split(', ').map(d => d.trim())
      : [];

    const travelApprovedStatuses = request.travel_approved_statuses
      ? request.travel_approved_statuses.split(', ').map(s => s.trim() === '1')
      : [];

    const totalTravelRecords = travelLocations.length;

    return {
      dates,
      days,
      types,
      totalDays,
      wfhdates,
      wfhdays,
      totalWFHDays,
      compWorkingDates,
      compOffDates,
      compDurations,
      compRequestTypes,
      compAmounts,
      totalCompDays,
      travelLocations,
      travelFromDatetimes,
      travelToDatetimes,
      travelApprovedStatuses,
      totalTravelRecords
    };
  };
  const formatDateTime = (datetimeString) => {
    if (!datetimeString) return '';
    try {
      const date = new Date(datetimeString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return datetimeString;
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const paginatedRequests = requests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const calculateTotalDays = (data) => {
    // If it's an array, process array
    if (Array.isArray(data)) {
      return data.reduce((total, rec) => {
        if (!rec?.leaveDay) return total + 1;
        return rec.leaveDay.toLowerCase() === "half" ? total + 0.5 : total + 1;
      }, 0);
    }

    if (typeof data === 'object' && data !== null && data.leave_type == "Work From Home Request") {
      const leaveDetails = parseLeaveDetails(data);
      return leaveDetails.totalWFHDays;
    }

    // If it's an object (like selected), parse the leave details
    if (typeof data === 'object' && data !== null) {
      const leaveDetails = parseLeaveDetails(data);
      return leaveDetails.totalDays;
    }



    return 0;
  };

  return (
    <>
      <ToastContainer />

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
          <p className="mt-2 text-muted small">Loading requests...</p>
        </div>
      ) : error ? (
        <Alert variant="danger" className="py-2">
          <small>{error}</small>
        </Alert>
      ) : requests.length === 0 ? (
        <Alert variant="info" className="py-2">
          <small>No pending requests</small>
        </Alert>
      ) : (
        <div>
          <Row className="g-2">
            {paginatedRequests.map((r, idx) => {
              const color = BORDER_COLORS[r.leave_type] || BORDER_COLORS.Other;
              const globalIndex = (currentPage - 1) * itemsPerPage + idx + 1;
              const time = new Date(r.created_at).toLocaleString();
              return (
                <Col key={r.request_id} xs={12} md={6} lg={4}>
                  <Card
                    className="h-100 position-relative shadow-sm"
                    style={{
                      borderLeft: `6px solid ${color}`,
                      cursor: "pointer",
                      transition: "transform 0.1s ease-in-out",
                      paddingTop: "0.5rem",
                    }}
                    onClick={() =>
                      setSelected({ ...r, displayNumber: globalIndex })
                    }
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.02)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1.0)")
                    }
                  >
                    <Badge
                      bg="light"
                      text="dark"
                      className="position-absolute"
                      style={{
                        top: "0.5rem",
                        left: "0.5rem",
                        fontSize: "0.75rem",
                        padding: "0.25em 0.5em",
                      }}
                    >
                      #{globalIndex}
                    </Badge>
                    <Card.Body className="pt-4 pb-2 px-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="warning">{r.leave_type}</Badge>
                        <small
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {time}
                        </small>
                      </div>
                      <Card.Title className="mb-1" style={{ fontSize: "1rem" }}>
                        {r.from_name}
                      </Card.Title>
                      <Card.Text
                        className="mb-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        <strong>To:</strong> {r.to_email}
                      </Card.Text>
                      <Card.Text
                        className="mb-1"
                        style={{ fontSize: "0.875rem" }}
                      >
                        <strong>Subject:</strong> {r.subject}
                      </Card.Text>
                      <Card.Text
                        style={{
                          height: "2.5rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "0.875rem",
                        }}
                      >
                        <strong>Body:</strong> {r.body || ""}
                      </Card.Text>
                    </Card.Body>
                  </Card>

                </Col>
              );
            })}
          </Row>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {[...Array(Math.min(3, totalPages))].map((_, i) => {
                  let pageNum;
                  if (currentPage <= 2) pageNum = i + 1;
                  else if (currentPage >= totalPages - 1) pageNum = totalPages - 2 + i;
                  else pageNum = currentPage - 1 + i;

                  if (pageNum < 1 || pageNum > totalPages) return null;

                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      )}

      {/* Request Detail Modal - Compact but Complete */}
      <Modal
        show={!!selected}
        onHide={() => setSelected(null)}
        size="lg"
        centered
        dialogClassName="modal-compact"
      >
        {selected && (
          <>
            <Modal.Header className="py-2">
              <Modal.Title className="fs-6">
                <div className="d-flex align-items-center">
                  <div>
                    <div className="fw-bold">{selected.from_name}</div>
                    <small className="text-muted">{selected.subject}</small>
                  </div>
                </div>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-2">
              {/* Basic Info */}
              <div className="mb-3">
                <div>
                  <h6 className="smal mb-1">Recipients</h6>
                  <div className="small">
                    <div className="mb-1">
                      <strong>From:</strong> {selected.from_email}
                    </div>
                    <div className="mb-1">
                      <strong>To:</strong> {selected.to_email}
                    </div>
                    {selected.cc_email && (
                      <div>
                        <strong>CC:</strong> {selected.cc_email}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Leave Details - Ultra Compact Table */}
              {selected.leave_dates && selected.leave_dates.length > 0 && !selected.leave_type?.includes('Compensatory') && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="small mb-0">Leave Details</h6>
                    <Badge bg="info" className="small px-2 py-1">
                      {calculateTotalDays(selected)} day{calculateTotalDays(selected) !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
                    <div className="row g-0 border-bottom bg-light">
                      <div className="col-5 py-1 border-end px-2 fw-bold text-dark">Date</div>
                      <div className="col-3 py-1 border-end text-center fw-bold text-dark">Type</div>
                      <div className="col-3 py-1 text-center fw-bold text-dark">Day</div>
                    </div>

                    {parseLeaveDetails(selected).dates.map((date, index) => {
                      const dayName = parseLeaveDetails(selected).days[index] || "Full";
                      const leaveType = parseLeaveDetails(selected).types[index];

                      return (
                        <div key={index} className="row g-0 border-bottom">
                          <div className="col-5 py-1 border-end px-2 fw-medium">
                            {formatDate(date)}
                          </div>
                          <div className="col-3 py-1 border-end text-center">
                            {leaveType}
                          </div>
                          <div className="col-3 py-1 text-center">
                            {dayName}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {selected.compensatory_working_dates && selected.compensatory_working_dates.length > 0 && selected.leave_type?.includes('Compensatory') && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="small mb-0">Compensatory Off Details</h6>
                    <Badge bg="warning" className="small px-2 py-1">
                      {parseLeaveDetails(selected).totalCompDays} day
                      {parseLeaveDetails(selected).totalCompDays !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
                    <div className="row g-0 border-bottom bg-light">
                      <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Working Date</div>
                      <div className="col-4 py-1 border-end text-center fw-bold text-dark">Comp Off Date</div>
                      <div className="col-4 py-1 border-end text-center fw-bold text-dark">Duration</div>
                    </div>

                    {parseLeaveDetails(selected).compWorkingDates.map((workingDate, index) => {
                      const compDetails = parseLeaveDetails(selected);
                      return (
                        <div key={index} className="row g-0 border-bottom">
                          <div className="col-4 py-1 border-end px-2 fw-medium">
                            {formatDate(workingDate)}
                          </div>
                          <div className="col-4 py-1 border-end text-center">
                            {compDetails.compOffDates[index]
                              ? formatDate(compDetails.compOffDates[index])
                              : 'Not specified'}
                          </div>
                          <div className="col-4 py-1 border-end text-center">
                            {compDetails.compDurations[index] || 'Full Day'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Travel (Site Visit) Details */}
              {selected.travel_locations && selected.travel_locations.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="small mb-0">Travel (Site Visit) Details</h6>
                    <Badge bg="warning" text="dark" className="small px-2 py-1">
                      {parseLeaveDetails(selected).totalTravelRecords} record
                      {parseLeaveDetails(selected).totalTravelRecords !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
                    {/* Header */}
                    <div className="row g-0 border-bottom bg-light">
                      <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Location</div>
                      <div className="col-4 py-1 border-end text-center fw-bold text-dark">From</div>
                      <div className="col-4 py-1 border-end text-center fw-bold text-dark">To</div>
                    </div>

                    {/* Rows */}
                    {parseLeaveDetails(selected).travelLocations.map((location, index) => {
                      const travelDetails = parseLeaveDetails(selected);
                      const isApproved = travelDetails.travelApprovedStatuses[index];

                      return (
                        <div key={index} className="row g-0 border-bottom">
                          <div className="col-4 py-1 border-end px-2 fw-medium">
                            {location}
                          </div>
                          <div className="col-4 py-1 border-end text-center">
                            {formatDateTime(travelDetails.travelFromDatetimes[index])}
                          </div>
                          <div className="col-4 py-1 border-end text-center">
                            {formatDateTime(travelDetails.travelToDatetimes[index])}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Leave Details - Ultra Compact Table */}
              {selected.wfh_dates && selected.wfh_dates.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="small mb-0">WFH Details</h6>
                    <Badge bg="info" className="small px-2 py-1">
                      {calculateTotalDays(selected)} day{calculateTotalDays(selected) !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
                    <div className="row g-0 border-bottom bg-light">
                      <div className="col-5 py-1 border-end px-2 fw-bold text-dark">Date</div>
                      <div className="col-3 py-1 text-center fw-bold text-dark">Day</div>
                    </div>

                    {parseLeaveDetails(selected).wfhdates.map((date, index) => {
                      const dayName = parseLeaveDetails(selected).wfhdays[index] || "Full";

                      return (
                        <div key={index} className="row g-0 border-bottom">
                          <div className="col-5 py-1 border-end px-2 fw-medium">
                            {formatDate(date)}
                          </div>
                          <div className="col-3 py-1 text-center">
                            {dayName}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Email Content */}
              <div className="mb-3">
                <h6 className="small  mb-1">Message</h6>
                <div
                  className="p-2 bg-light rounded small border"
                  style={{
                    whiteSpace: "pre-wrap",
                    maxHeight: "150px",
                    overflowY: "auto"
                  }}
                >
                  {selected.body}
                </div>
              </div>

              {/* Recipients */}

            </Modal.Body>

            <Modal.Footer className="py-2">
              <div className="d-flex gap-2 w-100">
                <Button
                  variant="outline-secondary"
                  onClick={() => setSelected(null)}
                  size="sm"
                  className="flex-grow-1"
                >
                  Close
                </Button>
                <Button
                  variant="success"
                  onClick={() => openEmailEditor("approved")}
                  size="sm"
                  className="flex-grow-1 d-flex align-items-center justify-content-center"
                  disabled={processing.has(selected.request_id)}
                >
                  <FaCheckCircle className="me-1" />
                  Approve
                </Button>
                <Button
                  variant="danger"
                  onClick={() => openEmailEditor("rejected")}
                  size="sm"
                  className="flex-grow-1 d-flex align-items-center justify-content-center"
                  disabled={processing.has(selected.request_id)}
                >
                  <FaTimesCircle className="me-1" />
                  Reject
                </Button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Email Modal - Compact */}
      <Modal
        show={emailModalVisible}
        onHide={() => setEmailModalVisible(false)}
        centered
        size="md"
      >
        <Modal.Header className="py-2">
          <Modal.Title className="fs-6 d-flex align-items-center">
            {decisionType === "approved" ? (
              <>
                <FaCheckCircle className="me-2 text-success" />
                Approve Request
              </>
            ) : (
              <>
                <FaTimesCircle className="me-2 text-danger" />
                Reject Request
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Subject</Form.Label>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                size="sm"
                className="border"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small fw-semibold">Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                size="sm"
                className="border"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <div className="d-flex gap-2 w-100">
            <Button
              variant="outline-secondary"
              onClick={() => setEmailModalVisible(false)}
              disabled={modalLoading}
              size="sm"
              className="flex-grow-1"
            >
              Cancel
            </Button>
            <Button
              variant={decisionType === "approved" ? "success" : "danger"}
              onClick={handleSendEmail}
              disabled={modalLoading}
              size="sm"
              className="flex-grow-1 d-flex align-items-center justify-content-center"
            >
              {modalLoading ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                <>
                  {decisionType === "approved" ? (
                    <FaCheckCircle className="me-1" />
                  ) : (
                    <FaTimesCircle className="me-1" />
                  )}
                  Send {decisionType === "approved" ? "Approval" : "Rejection"}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}